/**
 * Manta — rays gliding the reef and the fore-reef slope.
 *
 * Thrust comes off the downstroke of the same wing wave the shader is drawing, and drag is
 * very low — a manta covers an enormous distance between beats. That coast is most of what
 * separates it from a fish: it is not swimming, it is flying, and it barely has to.
 *
 * THEY FLY THE GROUND, NOT THE WATER COLUMN
 * Altitude is held above the SEABED rather than as a fraction of the depth, so a manta rises
 * over the reef and settles into the channels instead of tracking a level that has nothing to
 * do with what is underneath it. Two details make that read as flying rather than as
 * following a heightmap: the terrain is sampled AHEAD along the track, so the animal starts
 * climbing before it reaches a slope; and the altitude is chased slowly, so it cuts the
 * corners of the ground the way something with momentum has to.
 */
import * as THREE from 'three';
import { Rng } from '../core/Rng.ts';
import { OCEAN } from '../data/tuning.ts';
import { MantaMaterial } from '../render/MantaMaterial.ts';
import { bedHeight, waterDepth, lagoonEdge, COAST } from '../world/Coast.ts';
import { buildManta } from '../world/manta/voxelManta.js';
import type { SkyEnv } from '../world/DayNight.ts';
import { QUALITY } from '../data/quality.ts';

/**
 * The generator's own voxel pitch, in MODEL units.
 *
 * What matters is the pitch in WORLD units, which is this times the scale each ray is built
 * at — and the ray is scaled up far more than anything else, so a shared model pitch does not
 * give a shared grain. At 0.028 the rays came out at 0.086 u a voxel against the shark's
 * 0.033 and the turtle's 0.032–0.064: visibly the coarsest thing in the water and the only
 * animal whose voxels you could count.
 *
 * 0.013 lands them at 0.040 u, inside the turtle's range and a fifth off the shark. It is not
 * free — see the note on the instance count below.
 */
const VOX = QUALITY.mantaVox;
/** model wingspan, measured from the build */
const MODEL_SPAN = 2.20;
/* Reef-scale rather than oceanic: an animal that flies the seabed wants to be small enough
   that the reef is terrain rather than a texture under it. A 13 u wingspan also put the world
   voxel pitch at 0.165 u, six times the turtle's.

   NOTE what this costs, because it is the only thing here that is expensive. The ray is drawn
   as INSTANCED BOXES, not greedy-meshed like the shark, so every voxel is twelve triangles
   that no amount of flat shared surface will ever merge. The count scales with surface area,
   so halving the pitch roughly quadruples it: 3.2k voxels at 0.028, 16.1k at 0.013 — 580k
   triangles across the three rays, against 39k before, and a one-off 230 ms build at boot
   instead of 33 ms. Greedy meshing this the way SharkForge does would take back most of it
   and is the right move if the rays ever need to be cheaper. */
const SPAN_RANGE: [number, number] = [5.5, 8.0];

const WINDOW_X = 190;

interface Pose { amp: number; rate: number; lobe: number; pitch: number }
const BEH: Record<string, Pose> = {
  cruise: { amp: 0.30, rate: 1.00, lobe: 0.0, pitch: 0.00 },
  glide: { amp: 0.05, rate: 0.24, lobe: 0.0, pitch: -0.03 },
  feed: { amp: 0.22, rate: 0.72, lobe: 1.0, pitch: 0.04 },
};
/* Weighted hard to gliding, because that is what they mostly do. No somersault: it is a
   dramatic move, and a dramatic move on a loop stops being dramatic by the second time you
   see it. Without it the animal is only ever doing the one thing it should be doing. */
const ORDER = ['glide', 'cruise', 'glide', 'feed', 'glide', 'glide', 'cruise', 'feed'];
const DWELL: Record<string, [number, number]> = {
  cruise: [9, 18], glide: [14, 28], feed: [10, 20],
};

/* Cross-shore homes. Wider than before: they work the back-reef and the crest as well as the
   slope, because the point of flying the ground is to have ground worth flying over, and the
   reef is where the relief is. */
const HOMES: Array<[number, number]> = [[62, 116], [88, 130], [70, 124]];

/** how far ahead along the track the ground is sampled, in body spans */
const LOOKAHEAD = 1.6;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

interface Individual {
  root: THREE.Group;
  mesh: THREE.InstancedMesh;
  material: MantaMaterial;
  scale: number;
  p: THREE.Vector3;
  v: THREE.Vector3;
  heading: number;
  beh: string;
  behIdx: number;
  dwell: number;
  live: Pose & { bank: number };
  stroke: number;
  band: [number, number];
  /** preferred altitude above the seabed, u */
  ride: number;
  /** slow breathing on that altitude, so it is never pinned exactly to the ground */
  bobA: number;
  bobB: number;
  wanderA: number;
  wanderB: number;
  t: number;
}

export class Manta {
  readonly group = new THREE.Group();

  private readonly individuals: Individual[] = [];
  private readonly rng: Rng;
  private readonly geometry: THREE.BufferGeometry;
  private readonly voxels: number;

  constructor(count = 3, seed = 0x3a17a) {
    this.rng = new Rng(seed >>> 0);
    this.group.name = 'mantas';

    const D = buildManta({ vox: VOX, seed: this.rng.int(1, 1 << 28), span: 1, depth: 1, tail: 1, tipr: 0.55 });
    this.voxels = D.n;

    this.geometry = new THREE.BoxGeometry(1, 1, 1);
    const rest = new Float32Array(D.n * 3);
    const uv = new Float32Array(D.n * 2);
    const col = new Float32Array(D.n * 3);
    for (let i = 0; i < D.n; i++) {
      rest[i * 3] = D.x[i]; rest[i * 3 + 1] = D.y[i]; rest[i * 3 + 2] = D.z[i];
      uv[i * 2] = D.u[i]; uv[i * 2 + 1] = D.v[i];
      // Display space in, linear out: this pipeline shades in linear with ACES on the end.
      col[i * 3] = toLinear(D.cr[i]);
      col[i * 3 + 1] = toLinear(D.cg[i]);
      col[i * 3 + 2] = toLinear(D.cbl[i]);
    }
    this.geometry.setAttribute('aRest', new THREE.InstancedBufferAttribute(rest, 3));
    this.geometry.setAttribute('aUV', new THREE.InstancedBufferAttribute(uv, 2));
    this.geometry.setAttribute('aCol', new THREE.InstancedBufferAttribute(col, 3));
    this.geometry.setAttribute('aPart', new THREE.InstancedBufferAttribute(D.part, 1));
    this.geometry.setAttribute('aS', new THREE.InstancedBufferAttribute(D.s, 1));
    this.geometry.setAttribute('aRole', new THREE.InstancedBufferAttribute(D.role, 1));
    this.geometry.setAttribute('aShade', new THREE.InstancedBufferAttribute(D.shade, 1));

    const identity = new THREE.Matrix4();
    for (let i = 0; i < count; i++) {
      const scale = this.rng.range(SPAN_RANGE[0], SPAN_RANGE[1]) / MODEL_SPAN;
      const material = new MantaMaterial(VOX, scale);
      material.uniforms.uW.value = D.W;
      material.uniforms.uTone.value = this.rng.range(0.16, 0.52);
      material.uniforms.uPatch.value = this.rng.range(0.55, 0.90);
      material.uniforms.uSpots.value = this.rng.range(0.75, 1.3);

      const mesh = new THREE.InstancedMesh(this.geometry, material, D.n);
      for (let k = 0; k < D.n; k++) mesh.setMatrixAt(k, identity);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.frustumCulled = false;

      const root = new THREE.Group();
      root.scale.setScalar(scale);
      root.rotation.order = 'YXZ';
      root.add(mesh);
      this.group.add(root);

      const a: Individual = {
        root, mesh, material, scale,
        p: new THREE.Vector3(), v: new THREE.Vector3(),
        heading: this.rng.range(0, Math.PI * 2),
        beh: 'glide', behIdx: 0, dwell: this.rng.range(4, 14),
        live: { amp: 0.30, rate: 1, lobe: 0, pitch: 0, bank: 0 },
        stroke: this.rng.range(0, Math.PI * 2),
        band: HOMES[this.rng.int(0, HOMES.length - 1)],
        ride: this.rng.range(3.0, 8.0),
        bobA: this.rng.range(0.030, 0.055),
        bobB: this.rng.range(0.071, 0.113),
        wanderA: this.rng.range(0.07, 0.14),
        wanderB: this.rng.range(0.19, 0.31),
        t: this.rng.range(0, 60),
      };
      this.individuals.push(a);
      this.place(a, 0, true);
    }
  }

  get voxelCount(): number {
    return this.voxels;
  }

  get count(): number {
    return this.individuals.length;
  }

  update(dt: number, time: number, playerX: number): void {
    for (const a of this.individuals) {
      a.t += dt;
      this.behave(a, dt);
      this.locomote(a, dt);
      this.pose(a, dt, time);
      if (Math.abs(a.p.x - playerX) > WINDOW_X) this.place(a, playerX, false);
    }
  }

  private place(a: Individual, playerX: number, initial: boolean): void {
    const off = initial
      ? this.rng.range(-WINDOW_X, WINDOW_X)
      : -Math.sign(a.p.x - playerX) * (WINDOW_X - 26);
    // Recycled inside the bay: a spot chosen blind from the player would land on a headland.
    a.p.x = Math.max(-COAST.lagoonHalf + 40, Math.min(COAST.lagoonHalf - 40, playerX + off));
    a.p.z = this.rng.range(a.band[0], a.band[1]);
    if (waterDepth(a.p.x, a.p.z) < 7) a.p.z += 12;
    a.p.y = this.rideY(a);
    a.v.set(0, 0, 0);
    a.heading = initial ? this.rng.range(0, Math.PI * 2) : (off < 0 ? Math.PI / 2 : -Math.PI / 2);
  }

  /**
   * The height it wants to be flying at, over the ground it is about to be over.
   *
   * The look-ahead is the whole trick. Sampling only underfoot means the animal reacts to a
   * slope it is already on, so it gets shoved up by the terrain; taking the HIGHER of here
   * and a body-length ahead means it starts climbing before the slope arrives and holds its
   * height a moment after cresting. That asymmetry is exactly what a glider does.
   */
  private rideY(a: Individual): number {
    const look = LOOKAHEAD * MODEL_SPAN * a.scale;
    const bed = Math.max(
      bedHeight(a.p.x, a.p.z),
      bedHeight(a.p.x + Math.sin(a.heading) * look, a.p.z + Math.cos(a.heading) * look),
    );
    // Slow breathing on the altitude — two incommensurate periods, so it never repeats and
    // the animal is never welded to the contour.
    const bob = Math.sin(a.t * a.bobA * Math.PI * 2) * 0.9
              + Math.sin(a.t * a.bobB * Math.PI * 2 + 2.1) * 0.5;
    const want = bed + a.ride + bob * 0.9;
    // Wing clearance scales with the animal: a 13 u manta needs real room under it, and the
    // ceiling keeps the wingtips off the surface where the water gets thin.
    const minC = bed + 1.5 * a.scale;
    const maxC = OCEAN.surfaceHeight - 2.4 - 0.55 * a.scale;
    return Math.min(Math.max(want, minC), Math.max(minC, maxC));
  }

  private behave(a: Individual, dt: number): void {
    a.dwell -= dt;
    if (a.dwell <= 0) {
      a.behIdx = (a.behIdx + 1) % ORDER.length;
      a.beh = ORDER[a.behIdx];
      const d = DWELL[a.beh];
      a.dwell = this.rng.range(d[0], d[1]);
      // Feeding brings it down onto the reef; gliding lifts it back off.
      if (a.beh === 'feed') a.ride = this.rng.range(2.4, 4.2);
      else a.ride = this.rng.range(3.2, 8.5);
    }
  }

  private locomote(a: Individual, dt: number): void {
    const gy = bedHeight(a.p.x, a.p.z);

    let wantX = Math.sin(a.heading);
    let wantZ = Math.cos(a.heading);
    if (a.p.z < a.band[0]) wantZ += Math.min(1.3, (a.band[0] - a.p.z) * 0.08);
    else if (a.p.z > a.band[1]) wantZ -= Math.min(1.3, (a.p.z - a.band[1]) * 0.08);
    /* Wingspan sets the shallow limit, not a constant: a 13 u manta needs more water under it
       than a 10 u one, and the flap alone sweeps a couple of units. Scaled this way the big
       individuals stay out on the slope and the smaller ones can work in over the crest,
       which sorts the population across the reef without any rule saying so. */
    const need = 4.5 + 1.1 * a.scale;
    const shallow = need - waterDepth(a.p.x, a.p.z);
    if (shallow > 0) wantZ += shallow * 0.45;
    if (a.p.z > COAST.edgeZ + 4) wantZ -= (a.p.z - (COAST.edgeZ + 4)) * 0.25;
    /* The bay has ends. Steering off them by the same soft-pull mechanism as the cross-shore
       band means an animal curves away from a headland rather than pressing into it — and it
       keeps the population in the water the player is actually in. */
    const edge = lagoonEdge(a.p.x);
    if (edge > 0.02) wantX -= Math.sign(a.p.x) * edge * 2.2;
    const wob = Math.sin(a.t * a.wanderA * Math.PI * 2) * 0.6
              + Math.sin(a.t * a.wanderB * Math.PI * 2 + 1.1) * 0.3;

    let delta = Math.atan2(wantX, wantZ) + wob * 0.30 - a.heading;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    // Slow, wide turns. A manta banks a long way before its heading moves at all.
    const step = Math.max(-0.34 * dt, Math.min(0.34 * dt, delta));
    a.heading += step;
    const turnFrac = dt > 1e-5 ? step / dt / 0.34 : 0;
    a.live.bank += (turnFrac - a.live.bank) * (1 - Math.exp(-1.3 * dt));

    /* Thrust off the downstroke of the SAME wave the shader is drawing. Read it from a
       separate clock and the animal accelerates a fraction of a beat out of step with its own
       wings, which reads as slipping through the water. */
    const dn = Math.max(0, -Math.sin(a.stroke));
    const power = Math.pow(dn, 1.5) * a.live.amp * 3.1 * a.scale;
    a.v.x += Math.sin(a.heading) * power * dt;
    a.v.z += Math.cos(a.heading) * power * dt;
    a.v.y += a.live.pitch * (0.9 + power) * dt * a.scale;
    /* Chase the ride height SLOWLY. A stiff spring welds the animal to the ground and it
       reads as a heightmap follower; a slack one lets it cut the corners of the terrain,
       which is what something with this much mass and this little drag actually does. */
    a.v.y += ((this.rideY(a) - a.p.y) * 0.55 - a.v.y * 0.75) * dt;

    // Very low drag: a manta covers an enormous distance between beats, and that long coast
    // is most of what separates flying from swimming.
    a.v.multiplyScalar(Math.exp(-0.55 * dt));
    a.p.addScaledVector(a.v, dt);

    const floorY = gy + 1.3 * a.scale;
    if (a.p.y < floorY) { a.p.y = floorY; if (a.v.y < 0) a.v.y *= -0.10; }
    const ceil = OCEAN.surfaceHeight - 2.2;
    if (a.p.y > ceil) { a.p.y = ceil; if (a.v.y > 0) a.v.y = 0; }
  }

  private pose(a: Individual, dt: number, time: number): void {
    const T = BEH[a.beh];
    const ease = (r: number): number => 1 - Math.exp(-r * dt);
    const k = ease(1.8);
    a.live.amp = lerp(a.live.amp, T.amp, k);
    a.live.rate = lerp(a.live.rate, T.rate, k);
    a.live.lobe = lerp(a.live.lobe, T.lobe, ease(1.1));
    a.live.pitch = lerp(a.live.pitch, T.pitch, k);

    a.stroke += dt * a.live.rate * 1.55;

    const u = a.material.uniforms;
    u.uTime.value = time;
    u.uStroke.value = a.stroke;
    u.uAmp.value = a.live.amp;
    u.uBank.value = a.live.bank;
    u.uLobe.value = a.live.lobe;

    /* Pitch from the climb rate the animal is ACHIEVING, so it noses up going over a rise and
       down off the back of it. With the altitude chased slowly, that pitch is a direct read of
       how hard the terrain is working on it — which is what makes the flight look considered
       rather than reactive. */
    const sp = Math.hypot(a.v.x, a.v.z);
    const pitch = Math.max(-0.30, Math.min(0.30, -a.v.y / Math.max(sp, 1.0) * 0.55));
    a.root.position.copy(a.p);
    a.root.rotation.set(pitch, a.heading, -a.live.bank * 0.42, 'YXZ');
  }

  /** feed the wake: position and a body-scaled radius, so a big animal parts a wide swathe */
  trackInto(wake: { track(k: number, x: number, y: number, z: number, r: number, t: number): void },
            base: number, now: number): void {
    for (let i = 0; i < this.individuals.length; i++) {
      const a = this.individuals[i];
      wake.track(base + i, a.p.x, a.p.y, a.p.z, 2.6 * a.scale, now);
    }
  }

  setEnv(env: SkyEnv): void {
    for (const a of this.individuals) a.material.setEnv(env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    for (const a of this.individuals) a.material.setShadow(map, matrix, strength);
  }

  debugRow(): string {
    const by: Record<string, number> = {};
    for (const a of this.individuals) by[a.beh] = (by[a.beh] ?? 0) + 1;
    return `${this.individuals.length} · ${Object.entries(by).map(([k, v]) => `${v} ${k}`).join(', ')}`;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.geometry.dispose();
    for (const a of this.individuals) {
      a.mesh.dispose();
      a.material.dispose();
    }
    this.individuals.length = 0;
  }
}

function toLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
